// src/services/db.ts
import type { PostgrestError } from "@supabase/supabase-js";
import { supabase } from "../lib/supabase";

/**
 * Pequeños helpers finos que envuelven los patrones repetidos con Supabase.
 * Mantienen el shape { data, error } original (sin lanzar), para no romper flujos existentes.
 */

export type ServiceResult<T> = {
  data: T | null;
  error: PostgrestError | null;
};

export function from(table: string) {
  return supabase.from(table);
}

export function select<T = unknown>(
  table: string,
  columns: string,
  match?: Record<string, string | number | boolean | null>
) {
  let q = from(table).select<T>(columns);
  if (match) {
    for (const [k, v] of Object.entries(match)) {
      if (v === undefined) continue;
      q = q.eq(k, v as never);
    }
  }
  return q;
}

export async function getById<T = unknown>(
  table: string,
  columns: string,
  id: string
): Promise<ServiceResult<T>> {
  const { data, error } = await from(table).select<T>(columns).eq("id", id).single();
  return { data, error };
}

export async function insertOne<T = unknown>(
  table: string,
  payload: Record<string, unknown>,
  returning = "*"
): Promise<ServiceResult<T>> {
  const { data, error } = await from(table).insert(payload).select(returning).single();
  return { data, error };
}

export async function updateById<T = unknown>(
  table: string,
  id: string,
  payload: Record<string, unknown>,
  returning = "*"
): Promise<ServiceResult<T>> {
  const { data, error } = await from(table).update(payload).eq("id", id).select(returning).single();
  return { data, error };
}

export async function removeById(
  table: string,
  id: string
): Promise<ServiceResult<{ id: string }>> {
  const { data, error } = await from(table).delete().eq("id", id).select("id").single();
  return { data, error };
}
